import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { formatError } from "helpers/formatError";
import { swoogoRegistrantKeys } from "routes/swoogo/queryKeys";
import SwoogoService from "services/Swoogo";

const removeSessions = (registrant, sessionIds) => ({
  ...registrant,
  sessions: registrant.sessions.filter(
    session => !sessionIds.includes(session.id)
  ),
});

/**
 * Starts the task that removes the registrants from sessions in Swoogo. The
 * task runs in the background, so a refetch would still return the removed
 * sessions; the cached registrants drop them instead.
 */
const useRemoveSessionRegistrants = (registrants, onSuccess) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sessionIds =>
      SwoogoService.removeSessionRegistrants(
        sessionIds,
        registrants.map(registrant => registrant.id)
      ),
    onSuccess: (_data, sessionIds) => {
      const registrantIds = registrants.map(registrant => registrant.id);
      queryClient.setQueriesData(
        { queryKey: swoogoRegistrantKeys.lists() },
        page =>
          page?.data
            ? {
                ...page,
                data: page.data.map(row =>
                  registrantIds.includes(row.id)
                    ? removeSessions(row, sessionIds)
                    : row
                ),
              }
            : page
      );
      registrants.forEach(registrant =>
        queryClient.setQueryData(
          swoogoRegistrantKeys.detail(registrant.id),
          detail => (detail ? removeSessions(detail, sessionIds) : detail)
        )
      );
      toast.success("Removal started. Updated data will appear shortly.");
      onSuccess();
    },
    onError: err =>
      toast.error(formatError(err, "Could not start the removal")),
  });
};

export default useRemoveSessionRegistrants;
